// Global "rental defaults" persisted across listing pages, language switches, etc.
// Intentionally stores only a small subset of form values (dates + add-ons + delivery/pickup + seats).
// Uses localStorage (bigger quota than sessionStorage) and fails open on quota/privacy errors.

const STORAGE_KEY = 'rentAnything_rentalDefaults::v1';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const safeJsonParse = raw => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const safeGetLocalStorage = key => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSetLocalStorage = (key, value) => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};

const safeRemoveLocalStorage = key => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

const asIsoMaybe = d => (d instanceof Date ? d.toISOString() : typeof d === 'string' ? d : null);

// Keep payload minimal and locale-independent. No translated labels.
export const pickRentalDefaultsFromValues = values => {
  const v = values || {};

  const bookingDates = v.bookingDates || null;
  const startDateIso = bookingDates?.startDate ? asIsoMaybe(bookingDates.startDate) : null;
  const endDateIso = bookingDates?.endDate ? asIsoMaybe(bookingDates.endDate) : null;

  const bookingStartTime = v.bookingStartTime ?? null; // timestamp string in current code
  const bookingEndTime = v.bookingEndTime ?? null;

  const seats = v.seats != null ? v.seats : null;

  const otherOptionalFees = Array.isArray(v.otherOptionalFees) ? v.otherOptionalFees : null;
  const requiredFees = Array.isArray(v.requiredFees) ? v.requiredFees : null;

  // Dynamic fee fields (usage-multiple) are keyed by feeType.
  // We can't know keys here, so include all primitive fields except known ones.
  const knownKeys = new Set([
    'bookingDates',
    'bookingStartTime',
    'bookingEndTime',
    'seats',
    'otherOptionalFees',
    'requiredFees',
    'pickupAtOwnerLocation',
    'returnAtOwnerLocation',
  ]);

  const dynamicFees = Object.keys(v).reduce((acc, key) => {
    if (knownKeys.has(key)) return acc;
    const val = v[key];
    const isPrimitive = val == null || ['string', 'number', 'boolean'].includes(typeof val);
    if (isPrimitive) {
      acc[key] = val;
    }
    return acc;
  }, {});

  return {
    seats,
    bookingDates:
      startDateIso && endDateIso ? { startDate: startDateIso, endDate: endDateIso } : null,
    bookingStartTime,
    bookingEndTime,
    otherOptionalFees,
    requiredFees,
    pickupAtOwnerLocation: v.pickupAtOwnerLocation ?? null, // array([true]) in code
    returnAtOwnerLocation: v.returnAtOwnerLocation ?? null, // array([true]) in code
    dynamicFees,
  };
};

export const saveRentalDefaults = (values, { ttlMs = DEFAULT_TTL_MS } = {}) => {
  const now = Date.now();
  const payload = {
    storedAt: now,
    expiresAt: now + ttlMs,
    values,
  };
  return safeSetLocalStorage(STORAGE_KEY, JSON.stringify(payload));
};

export const loadRentalDefaults = () => {
  const raw = safeGetLocalStorage(STORAGE_KEY);
  const parsed = safeJsonParse(raw);
  if (!parsed || !parsed.expiresAt || !parsed.values) return null;
  if (Date.now() > parsed.expiresAt) {
    safeRemoveLocalStorage(STORAGE_KEY);
    return null;
  }
  return parsed.values;
};

export const clearRentalDefaults = () => safeRemoveLocalStorage(STORAGE_KEY);

// Read-only helper for pages that only want to PREFILL a date range the user already
// confirmed elsewhere (search page, store profile page) - they never write this key.
// Returns Date objects, or null whenever there is nothing usable: no saved value, an
// expired TTL, corrupt JSON, unparseable dates, an end before the start, or a start date
// that has already gone by. Expired ranges from a previous session must not resurface
// even while the 24h TTL still has time left on it.
//
// "Past" is judged at day granularity on purpose: it mirrors the isOutsideRange rule the
// calendars on both pages already enforce (`isBefore(moment(), 'day')`), so a range
// starting today - still freely selectable in those calendars - still prefills, while a
// stricter instant comparison would silently drop it any time after midnight.
export const loadRentalDefaultDateRange = () => {
  const defaults = loadRentalDefaults();
  if (!defaults?.bookingDates?.startDate || !defaults?.bookingDates?.endDate) return null;

  const startDate = new Date(defaults.bookingDates.startDate);
  const endDate = new Date(defaults.bookingDates.endDate);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (startDate < startOfToday) return null;
  if (endDate < startDate) return null;

  return { startDate, endDate };
};


